/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const AdminDialogContext = createContext(null);

function normalizeOptions(input, defaults = {}) {
  if (typeof input === 'string') {
    return { message: input, ...defaults };
  }
  return { ...defaults, ...input };
}

function AdminDialogOverlay({ state }) {
  const dismiss = () => {
    if (state.kind === 'alert') {
      state.resolveAlert();
    } else if (state.kind === 'choice') {
      state.resolveChoice(null);
    } else {
      state.resolveConfirm(false);
    }
  };

  const handleConfirm = () => {
    if (state.kind === 'confirm') {
      state.resolveConfirm(true);
    } else {
      state.resolveAlert();
    }
  };

  const variantClass = state.variant === 'error'
    ? ' admin-dialog--error'
    : state.variant === 'success'
      ? ' admin-dialog--success'
      : state.variant === 'warning'
        ? ' admin-dialog--warning'
        : '';

  return (
    <div className="modal-overlay admin-dialog-overlay" onClick={dismiss}>
      <div
        className={`modal modal-wide admin-dialog${variantClass}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-dialog-title"
      >
        <div className="modal-header">
          <h2 id="admin-dialog-title">{state.title}</h2>
          <button type="button" className="modal-close" onClick={dismiss} aria-label="閉じる">
            &times;
          </button>
        </div>
        <div className="modal-body">
          <p className="admin-dialog-message">{state.message}</p>
        </div>
        <div className="modal-footer">
          {state.kind === 'choice' ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={dismiss}>
                {state.cancelLabel}
              </button>
              {state.choices.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`btn ${c.danger ? 'btn-danger' : c.primary ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => state.resolveChoice(c.value)}
                  autoFocus={c.primary === true}
                >
                  {c.label}
                </button>
              ))}
            </>
          ) : (
            <>
              {state.kind === 'confirm' && (
                <button type="button" className="btn btn-secondary" onClick={dismiss}>
                  {state.cancelLabel}
                </button>
              )}
              <button
                type="button"
                className={`btn ${state.kind === 'confirm' && state.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={handleConfirm}
                autoFocus
              >
                {state.kind === 'confirm' ? state.confirmLabel : 'OK'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminDialogProvider({ children }) {
  const [state, setState] = useState(null);

  const showAlert = useCallback((input) => {
    const opts = normalizeOptions(input, { title: 'お知らせ', variant: 'info' });
    return new Promise((resolve) => {
      setState({
        kind: 'alert',
        title: opts.title,
        message: String(opts.message ?? ''),
        variant: opts.variant || 'info',
        resolveAlert: () => {
          setState(null);
          resolve();
        },
      });
    });
  }, []);

  const showConfirm = useCallback((input) => {
    const opts = normalizeOptions(input, {
      title: '確認',
      confirmLabel: 'OK',
      cancelLabel: 'キャンセル',
      danger: false,
    });
    return new Promise((resolve) => {
      setState({
        kind: 'confirm',
        title: opts.title,
        message: String(opts.message ?? ''),
        confirmLabel: opts.confirmLabel,
        cancelLabel: opts.cancelLabel,
        danger: opts.danger === true,
        resolveConfirm: (value) => {
          setState(null);
          resolve(value);
        },
      });
    });
  }, []);

  /**
   * 3つ以上の選択肢から選ばせる。choices: [{ value, label, primary?, danger? }]
   * 選んだ value を返す。キャンセル・閉じる・背景クリックは null
   */
  const showChoice = useCallback((input) => {
    const opts = normalizeOptions(input, { title: '確認', cancelLabel: 'キャンセル', choices: [] });
    return new Promise((resolve) => {
      setState({
        kind: 'choice',
        title: opts.title,
        message: String(opts.message ?? ''),
        cancelLabel: opts.cancelLabel,
        choices: Array.isArray(opts.choices) ? opts.choices : [],
        resolveChoice: (value) => {
          setState(null);
          resolve(value);
        },
      });
    });
  }, []);

  const value = useMemo(() => ({ showAlert, showConfirm, showChoice }), [showAlert, showConfirm, showChoice]);

  return (
    <AdminDialogContext.Provider value={value}>
      {children}
      {state && <AdminDialogOverlay state={state} />}
    </AdminDialogContext.Provider>
  );
}

export function useAdminDialog() {
  const ctx = useContext(AdminDialogContext);
  if (!ctx) {
    throw new Error('useAdminDialog must be used within AdminDialogProvider');
  }
  return ctx;
}
